/**
 * celebrationCopy.ts — every word of the moment straight after a successful
 * payment: the celebration modal, and `accountStageHelp`, the account step's
 * route-aware opener. Since the account step's second pass (2026-09-24) only
 * its `'confirm_email'` line is rendered; the create screen's opener and the
 * two-step indicator live in `accountStepCopy.ts`.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey-ux L5
 * (post-payment celebration, CTO 2026-09-24; v2 the same day).
 *
 * ── THE MODAL SAYS NOTHING ABOUT MONEY (v2, CTO 2026-09-24) ────────────────
 * The first cut printed the route's money line ("We've placed a hold of £X on
 * your card…" / "Your first month's payment has been taken."). The CTO: "Even
 * in here, we shouldn't show the amounts or anything. We should simply say
 * 'Congratulations, you're on your journey to lose weight'." So the modal copy
 * takes NO amount and names no payment, hold or charge on EITHER route — the
 * card step already said it, the account step's own line (below) still says
 * the route's truth, and /thank-you says it again. `celebrationCopy` has no
 * amount parameter on purpose: a figure cannot be passed in, so it cannot be
 * printed.
 *
 * ── THE TRUST POINTS ARE CLAIMS THE SITE ALREADY MAKES, AND NOTHING NEW ────
 * The CTO asked for "something that improves their confidence and their trust".
 * On a regulated clinic that means true statements, not persuasion, so every
 * sentence below is one the portal already makes elsewhere, and none of them
 * carries a number:
 *  - "A UK clinician reviews your assessment" / "Usually within one working
 *    day" — `paymentCopy.ts` (HOLD_CONFIRMATION) and `journey/WhatHappensNext`,
 *    word for word; "nothing is prescribed without their approval" is what
 *    orders does (the clinician issues month 1; nothing auto-issues).
 *  - "GPhC-registered pharmacy" — the site footer (`ui/chrome.tsx`: "Dispensing
 *    by Pharmacy Xpress (GPhC-registered)"). The TrustStrip chip said "GMC/GPhC
 *    pharmacy" when this was written; a pharmacy registers with the GPhC only,
 *    and since review 2026-09-25 the chip says "GPhC-registered pharmacy" too.
 *  - "plain packaging" — the Result screen's "Discreet delivery" step.
 *  - "Our team is here to help … get in touch and we'll guide you" — the
 *    patient dashboard's support card.
 * NO review counts, ratings, star scores, percentages or promised timings
 * beyond the one existing "usually" — `celebration.spec.tsx` fails on any digit
 * or `%` in this copy. Adding a claim here means it must already be true and
 * already be said; it is not a place to write new marketing.
 *
 * ── THE ONE NUMBER IN IT IS 999, AND IT IS NOT A CLAIM ─────────────────────
 * The modal covers the page with a near-opaque scrim above the sticky chrome,
 * so the R31 ribbon ("Not for emergencies. Call 999 …") is unreadable behind it,
 * and `aria-modal` hides the ribbon from a screen reader outright. There is no
 * Escape and no click-away, so it stays hidden until the patient presses the
 * button. `ConsentBanner` (the other modal in this repo that cannot be
 * dismissed) restates the line inside itself for exactly that reason, and so
 * does this one: {@link CELEBRATION_EMERGENCY}, the same sentence word for word.
 * It is signposting, not proof or price, so the spec's no-digits rule covers
 * every OTHER line and checks this one by its exact text.
 *
 * ── THE ROUTE STILL MATTERS, FOR THE CLINICAL WORDS ─────────────────────────
 * On the HOLD route a clinician has NOT yet looked: the review is ahead, and
 * the pharmacy acts only "once approved". On the clinician-led (captured)
 * route the clinician already reviewed and approved the treatment on the
 * consultation (`paymentCopy.ts` IMMEDIATE_PAYMENT) — telling that patient a
 * clinician "reviews your assessment" would describe a step they have done.
 *
 * THE ROUTE IS THE SERVER'S. Callers pass the capture route derived from the
 * attach response's `paymentPhase` (`paymentPhaseToCaptureRoute`) — never a
 * query parameter, never a guess.
 */
import type { PaymentCaptureRoute } from './paymentCopy';

/** One reassurance point in the modal: a short bold line and one sentence under it. */
export type CelebrationTrustPoint = {
  /** Which icon sits beside it (decorative — the words carry the meaning). */
  icon: 'clinician' | 'pharmacy' | 'team';
  title: string;
  body: string;
};

export type CelebrationCopy = {
  /** The modal's heading (its accessible name). */
  heading: string;
  /** The warm line — the same on both routes, and the same as /thank-you's. */
  lead: string;
  /** The heading over the trust points (and the list's accessible name). */
  trustHeading: string;
  /** What happens now, and who looks after them — true claims only (see header). */
  trust: readonly CelebrationTrustPoint[];
  /** Why the account is the next step. */
  invite: string;
  /** The one button, and the modal's ONLY way out. */
  cta: string;
  /** The R31 emergency line the modal hides, restated inside it (see the header). */
  emergency: string;
};

/**
 * The warm line, shared by the modal and the /thank-you confirmation. Category
 * level on purpose (no medicine, no outcome promise): it says the patient has
 * STARTED, which is true the moment an order exists, whatever a clinician
 * later decides. The CTO's "you're on your journey to lose weight", kept to
 * the sentence /thank-you already shows so the two moments say one thing.
 */
export const CELEBRATION_LEAD = 'You’ve taken the first step of your weight-loss journey.';

/** The modal's heading on both routes — the CTO's own word. */
export const CELEBRATION_HEADING = 'Congratulations!';

/**
 * The one button. The CTO's own words: the modal cannot be dismissed, and this
 * is the way on — to the account form that is already on the screen under it.
 * First person because it is the patient's action, said the way they would say
 * it; "Finish" because the order is placed and the account is the one thing
 * left to do — on the form-first lane (a password) and the code-first lane (the
 * emailed code) alike.
 */
export const CELEBRATION_CTA = 'Finish setting up my account';

/**
 * The emergency signposting the modal covers up, said inside it. Word for word
 * the line `ConsentBanner` restates for the same reason, so the two modals that
 * cannot be dismissed say one thing about 999 (see the header).
 */
export const CELEBRATION_EMERGENCY = 'In an emergency, call 999.';

const TRUST_HEADING = 'What happens next';

const PHARMACY_TITLE = 'Prepared by a GPhC-registered pharmacy';

/**
 * Exported (2026-09-25) because the journey entry loader card's last chip says
 * the same thing in the same words — this TITLE, verbatim
 * (`journey/entryLoaderContent.ts`) — the one place the site already tells a
 * patient how our team supports them. Reword it here.
 */
export const TEAM_SUPPORT: CelebrationTrustPoint = {
  icon: 'team',
  title: 'Our team is here to help',
  body: 'Questions about your treatment or delivery? Get in touch and we’ll guide you.',
};

/** HOLD: nobody clinical has looked yet, so the review is ahead and the pharmacy waits for it. */
const HOLD_TRUST: readonly CelebrationTrustPoint[] = [
  {
    icon: 'clinician',
    title: 'A UK clinician reviews your assessment',
    body: 'Usually within one working day. Nothing is prescribed without their approval.',
  },
  {
    icon: 'pharmacy',
    title: PHARMACY_TITLE,
    body: 'Once approved, your treatment is sent to your door in plain packaging.',
  },
  TEAM_SUPPORT,
];

/** CAPTURED (clinician-led): the clinician already reviewed and approved it on the consultation. */
const CAPTURED_TRUST: readonly CelebrationTrustPoint[] = [
  {
    icon: 'clinician',
    title: 'Approved by your clinician',
    body: 'Your clinician has already reviewed and approved your treatment.',
  },
  {
    icon: 'pharmacy',
    title: PHARMACY_TITLE,
    body: 'Your treatment is sent to your door in plain packaging.',
  },
  TEAM_SUPPORT,
];

const INVITE = 'Your account is where you’ll follow your order and hear from your clinician.';

/**
 * The celebration modal's copy for a capture route. NO amount parameter — see
 * the header: the modal names no figure, payment, hold or charge on any route.
 */
export function celebrationCopy(route: PaymentCaptureRoute): CelebrationCopy {
  return {
    heading: CELEBRATION_HEADING,
    lead: CELEBRATION_LEAD,
    trustHeading: TRUST_HEADING,
    trust: route === 'immediate' ? CAPTURED_TRUST : HOLD_TRUST,
    invite: INVITE,
    cta: CELEBRATION_CTA,
    emergency: CELEBRATION_EMERGENCY,
  };
}

/**
 * The account step's opening line, per route and per screen.
 *
 * The captured route keeps "Payment is done" — it is true there. On the hold
 * route it was FALSE (no payment has been taken, only a hold placed), so it
 * says the order is placed instead (CTO 2026-09-24).
 *
 * ⚠️ ONLY `'confirm_email'` IS RENDERED. The create screen prints
 * `accountStepCopy.createAccountHelp` (the same opener, without "(or sign in)":
 * the sign-in link left that screen). The `'create'` branch is kept only
 * because `celebration.spec.tsx` still pins it; remove the two together.
 */
export function accountStageHelp(route: PaymentCaptureRoute, screen: 'create' | 'confirm_email'): string {
  const opener = route === 'immediate' ? 'Payment is done' : 'Your order is placed';
  if (screen === 'confirm_email') {
    return `${opener} — open the link we sent so we can save this plan to your account.`;
  }
  return route === 'immediate'
    ? `${opener} — create an account (or sign in) so we can save your plan and delivery details for next time.`
    : `${opener} — create your account (or sign in) so we can save your plan and delivery details for next time.`;
}
